// TODO: Improve me, crudely copied from DualTooltip.tsx and written by Claude

import { useState, useEffect, ReactElement, useRef } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useInteractions,
    useClick,
    FloatingPortal,
    FloatingArrow,
    Placement,
    useTransitionStatus,
    arrow,
    flip,
} from '@floating-ui/react'
import styles from './DualTooltipMealRelief.module.scss'
import clsx from 'clsx'

interface Props {
    children: ReactElement
    tooltip?: ReactElement
    placement?: Placement
    offset?: number
    trigger?: 'hover' | 'click' | 'manual' | 'hover+click'
    isShown?: boolean
    className?: string
    // New props for click tooltip
    clickTooltip?: ReactElement
    clickTooltipClassName?: string
}

export const DualTooltipMealRelief = ({
    children,
    placement = 'bottom',
    trigger = 'hover',
    offset: _offset = 10,
    isShown = true,
    className,
    tooltip,
    clickTooltip,
    clickTooltipClassName,
}: Props) => {
    const arrowRef = useRef(null)
    const [hoverOpen, setHoverOpen] = useState(false)
    const [clickOpen, setClickOpen] = useState(false)

    // Hover tooltip floating UI
    const hoverFloating = useFloating({
        open: hoverOpen,
        onOpenChange: (state) => {
            if (isShown && (trigger === 'hover' || trigger === 'hover+click')) {
                setHoverOpen(state)
            }
        },
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(_offset),
            flip(),
            shift(),
            arrow({
                element: arrowRef,
            }),
        ],
    })

    // Click tooltip floating UI - anchored to bottom right
    const clickFloating = useFloating({
        open: clickOpen,
        onOpenChange: (state) => {
            if (isShown && (trigger === 'click' || trigger === 'hover+click')) {
                setClickOpen(state)
                if (state) {
                    setHoverOpen(false)
                }
            }
        },
        strategy: 'fixed',
        placement: 'top-end',
        middleware: [offset({ mainAxis: 100, crossAxis: 100 }), flip()],
        transform: false, // We'll handle positioning manually
    })

    const hoverInteraction = useHover(hoverFloating.context, {
        move: false,
        enabled: trigger === 'hover' || trigger === 'hover+click',
    })

    const focusInteraction = useFocus(hoverFloating.context)
    const dismissHoverInteraction = useDismiss(hoverFloating.context)
    const clickInteraction = useClick(hoverFloating.context, {
        enabled: trigger === 'click' || trigger === 'hover+click',
    })

    const dismissClickInteraction = useDismiss(clickFloating.context)

    const { isMounted: hoverIsMounted, status: hoverStatus } = useTransitionStatus(hoverFloating.context)
    const { isMounted: clickIsMounted, status: clickStatus } = useTransitionStatus(clickFloating.context)

    const { getReferenceProps, getFloatingProps: getHoverFloatingProps } = useInteractions([
        focusInteraction,
        dismissHoverInteraction,
        hoverInteraction,
        ...(trigger === 'hover+click' ? [] : [clickInteraction]),
    ])

    const { getReferenceProps: getClickReferenceProps, getFloatingProps: getClickFloatingProps } = useInteractions([
        dismissClickInteraction,
        useClick(clickFloating.context, {
            enabled: trigger === 'hover+click',
        }),
    ])

    useEffect(() => {
        if (trigger === 'manual') {
            setHoverOpen(isShown)
        }
    }, [trigger, isShown])

    return (
        <>
            <div
                ref={hoverFloating.refs.setReference}
                {...getReferenceProps()}
                {...(trigger === 'hover+click' ? getClickReferenceProps() : {})}
            >
                {children}
            </div>

            {/* Hover tooltip */}
            {tooltip && hoverIsMounted && isShown && (trigger === 'hover' || trigger === 'hover+click') && (
                <FloatingPortal>
                    <div
                        ref={hoverFloating.refs.setFloating}
                        className={clsx(styles.container, className)}
                        style={{
                            position: hoverFloating.strategy,
                            top: hoverFloating.y ?? 0,
                            left: hoverFloating.x ?? 0,
                        }}
                        data-status={hoverStatus}
                        {...getHoverFloatingProps({ onClick: (e) => e.stopPropagation() })}
                    >
                        <FloatingArrow ref={arrowRef} context={hoverFloating.context} />
                        {tooltip}
                    </div>
                </FloatingPortal>
            )}

            {/* Click tooltip - anchored to bottom right */}
            {clickIsMounted && isShown && (trigger === 'click' || trigger === 'hover+click') && (
                <FloatingPortal>
                    <div
                        ref={clickFloating.refs.setFloating}
                        className={clsx(styles.container, clickTooltipClassName)}
                        style={{
                            position: 'fixed',
                            bottom: '20px',
                            right: '20px',
                            zIndex: 1000,
                        }}
                        data-status={clickStatus}
                        {...getClickFloatingProps({ onClick: (e) => e.stopPropagation() })}
                    >
                        {clickTooltip || tooltip}
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}
